/**
 * Main-thread owner for the Showroom building worker singleton.
 *
 * Keep this module free of runtime dependencies: it is bundled into the parser-blocking preload script.
 */
import type { ShowroomWorkerFailurePhase } from "./showroomBuildingWorkerMessages";

export interface ShowroomWorkerFailureMetadata {
  fallbackMessage?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
}

export interface TerminalShowroomWorkerFailure extends ShowroomWorkerFailureMetadata {
  error?: unknown;
  phase: ShowroomWorkerFailurePhase;
  sessionId?: string;
  reported: boolean;
}

export type ShowroomWorkerObservationState = "never" | "active" | "detached";

export interface ShowroomWorkerSingletonState {
  failure?: TerminalShowroomWorkerFailure;
  observed?: ShowroomWorkerObservationState;
  worker?: Worker;
}

declare global {
  interface Window {
    showroomWorkerSingleton?: ShowroomWorkerSingletonState;
  }
}

/** Returns the page-scoped Showroom worker state. */
export function getShowroomWorkerSingletonState(): ShowroomWorkerSingletonState {
  return (window.showroomWorkerSingleton ??= {});
}

function logShowroomWorkerLoadError(event: ErrorEvent): void {
  console.error("showroom worker failed", event.message, event.filename, event.lineno);
}

/** Preserves the first terminal worker failure until the page is refreshed. */
export function recordTerminalShowroomWorkerFailure(
  error: unknown,
  phase: ShowroomWorkerFailurePhase,
  sessionId?: string | null,
  metadata?: ShowroomWorkerFailureMetadata,
): TerminalShowroomWorkerFailure {
  const state = getShowroomWorkerSingletonState();
  if (!state.failure) {
    state.failure = {
      error,
      ...metadata,
      phase,
      sessionId: sessionId ?? undefined,
      reported: false,
    };
  }
  return state.failure;
}

function handleShowroomWorkerLoadError(worker: Worker, event: ErrorEvent): void {
  const state = getShowroomWorkerSingletonState();
  if (state.observed === "active") {
    return;
  }

  logShowroomWorkerLoadError(event);
  const phase = state.observed === "detached" ? "runtime" : "startup";
  recordTerminalShowroomWorkerFailure(event.error, phase, undefined, {
    fallbackMessage: event.message || "Worker error",
    filename: event.filename || undefined,
    lineno: event.lineno || undefined,
    colno: event.colno || undefined,
  });
  discardShowroomWorker(worker);
}

/** Registers a newly constructed worker as the page singleton. */
export function registerShowroomWorker(worker: Worker): Worker {
  // Keep this listener for failures before or between hook mounts. The hook adds its own runtime listener while mounted.
  worker.addEventListener("error", (event) => handleShowroomWorkerLoadError(worker, event));
  const state = getShowroomWorkerSingletonState();
  state.worker = worker;
  state.observed = "never";
  return worker;
}

/** Creates the worker during HTML parsing so its module starts downloading immediately. */
export function preloadShowroomWorker(workerUrl: string): Worker | null {
  const state = getShowroomWorkerSingletonState();
  if (state.failure) {
    return null;
  }
  if (state.worker) {
    return state.worker;
  }

  try {
    const worker = new Worker(workerUrl, { type: "module" });
    return registerShowroomWorker(worker);
  } catch (error: unknown) {
    console.warn("Failed to preload showroom worker", error);
    recordTerminalShowroomWorkerFailure(error, "startup", undefined, {
      fallbackMessage: "Failed to start showroom worker",
    });
    return null;
  }
}

/** Removes and terminates the worker when it is still the active singleton. */
export function discardShowroomWorker(worker: Worker): void {
  const state = getShowroomWorkerSingletonState();
  if (state.worker === worker) {
    delete state.worker;
  }
  worker.terminate();
}

/** Records the hook's relationship to the active worker's error events. */
export function setShowroomWorkerObserved(observed: ShowroomWorkerObservationState): void {
  getShowroomWorkerSingletonState().observed = observed;
}
